export class Duration {
    private seconds: number;
    private nanos: number;

    constructor(seconds: number, nanos: number = 0) {
        this.seconds = seconds;
        this.nanos = nanos;
        this.normalize();
    }

    private normalize() {
        if (this.nanos >= 1e9) {
            this.seconds += Math.floor(this.nanos / 1e9);
            this.nanos %= 1e9;
        } else if (this.nanos < 0) {
            const nanosecondsToRemove = Math.ceil(Math.abs(this.nanos) / 1e9);
            this.seconds -= nanosecondsToRemove;
            this.nanos += nanosecondsToRemove * 1e9;
        }
    }

    static ofMilliseconds(milliseconds: number): Duration {
        return Duration.ofSeconds(milliseconds / 1000);
    }

    static ofSeconds(seconds: number, nanoAdjustment: number = 0): Duration {
        const adjustedSeconds = seconds + Math.floor(nanoAdjustment / 1e9);
        const adjustedNanos = nanoAdjustment % 1e9;
        return new Duration(adjustedSeconds, adjustedNanos);
    }

    static ofMinutes(minutes: number): Duration {
        return Duration.ofSeconds(minutes * 60);
    }

    static ofHours(hours: number): Duration {
        return Duration.ofMinutes(hours * 60);
    }

    static ofDays(days: number): Duration {
        return Duration.ofHours(days * 24);
    }

    static between(start: Date, end: Date): Duration {
        const diffMs = end.getTime() - start.getTime();
        const seconds = Math.floor(diffMs / 1000);
        const nanos = (diffMs % 1000) * 1e6;
        return new Duration(seconds, nanos);
    }

    static parse(durationString: string): Duration {
        const regex = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;
        const match = (durationString ?? "").match(regex);

        if (!match) {
            return new Duration(0, 0);
        }

        const [, years, months, days, hours, minutes, seconds] = match;
        let totalSeconds = 0;

        if (years) totalSeconds += parseInt(years) * 365 * 24 * 60 * 60;
        if (months) totalSeconds += parseInt(months) * 30 * 24 * 60 * 60; // Approximation
        if (days) totalSeconds += parseInt(days) * 24 * 60 * 60;
        if (hours) totalSeconds += parseInt(hours) * 60 * 60;
        if (minutes) totalSeconds += parseInt(minutes) * 60;
        if (seconds) {
            const [secondsPart, nanosPart] = seconds.split('.');
            totalSeconds += parseInt(secondsPart);
            const nanos = nanosPart ? parseInt(nanosPart.padEnd(9, '0')) : 0;
            return new Duration(totalSeconds, nanos);
        }

        return new Duration(totalSeconds);
    }


    getRemainingSeconds(): number {
        return this.seconds % 60;
    }

    getDays(mode: string[] = ["D"]): number {
        return mode.includes("D") ? Math.floor(this.seconds / (24 * 60 * 60)) : 0;
    }

    getHours(mode: string[] = ["H"]): number {
        if (!mode.includes("H")) return 0;
        if (!mode.includes("D")) return Math.floor(this.seconds / (60 * 60));
        return Math.floor((this.seconds % (24 * 60 * 60)) / (60 * 60));
    }

    getMinutes(mode: string[] = ["M"]): number {
        if (!mode.includes("M")) return 0;
        if (!mode.includes("H")) return Math.floor(this.seconds / 60);
        return Math.floor((this.seconds % (60 * 60)) / 60);
    }

    getSeconds(mode: string[] = ["S"]): number {
        if (!mode.includes("S")) return 0;
        if (!mode.includes("M")) return this.seconds;
        return this.seconds % 60;
    }


    setDays(days: number, bounds: boolean = true): void {
        const remainingSeconds = this.seconds % (24 * 60 * 60);
        this.seconds = days * 24 * 60 * 60 + remainingSeconds;
    }

    setHours(hours: number, mode: string[], bounds: boolean = true): void {
        if (bounds) {
            if (hours < 0 || hours > 23) {
                hours = 23
            }
            const days = this.getDays(mode);
            const remainingSeconds = this.seconds % (60 * 60);
            this.seconds = days * 24 * 60 * 60 + hours * 60 * 60 + remainingSeconds;
        } else {
            let days = this.getDays(mode);
            let remainingSeconds = this.seconds % (60 * 60);
            if (hours > 23) {
                days = 0
                remainingSeconds = 0
            }
            this.seconds = days * 24 * 60 * 60 + hours * 60 * 60 + remainingSeconds;

        }
    }

    setMinutes(minutes: number, mode: string[], bounds: boolean = true): void {
        if (bounds) {
            if (minutes < 0 || minutes > 59) {
                minutes = 59
            }
            const days = this.getDays(mode);
            const hours = this.getHours(mode);
            const remainingSeconds = this.getRemainingSeconds();
            this.seconds = days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + remainingSeconds;
        } else {
            let days = this.getDays(mode);
            let hours = this.getHours(mode);
            let remainingSeconds = this.getRemainingSeconds();
            if (minutes > 59) {
                days = 0
                hours = 0
                remainingSeconds = 0
            }
            this.seconds = days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + remainingSeconds;
        }
    }

    setSeconds(seconds: number, mode: string[], bounds: boolean = true): void {
        if (bounds) {
            if (seconds < 0 || seconds > 59) {
                seconds = 59
            }
            const days = this.getDays(mode);
            const hours = this.getHours(mode);
            const minutes = this.getMinutes(mode);
            this.seconds = days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds;
        } else {
            let days = this.getDays(mode);
            let hours = this.getHours(mode);
            let minutes = this.getMinutes(mode);
            if (seconds > 59) {
                days = 0
                hours = 0
                minutes = 0
            }
            this.seconds = days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds;
        }
    }

    setNano(nanos: number, bounds: boolean = true): void {
        if (bounds) {
            if (nanos < 0 || nanos > 999999999) {
                nanos = 999999999
            }
        }
        this.nanos = nanos;
    }

    toMillis(): number {
        return this.seconds * 1000 + Math.floor(this.nanos / 1e6);
    }

    plus(other: Duration): Duration {
        return new Duration(this.seconds + other.seconds, this.nanos + other.nanos);
    }

    minus(other: Duration): Duration {
        return new Duration(this.seconds - other.seconds, this.nanos - other.nanos);
    }

    toString(): string {
        return this.toISOString();
    }

    toISOString(): string {
        let result = 'P';
        const years = Math.floor(this.seconds / (365 * 24 * 60 * 60));
        const months = Math.floor((this.seconds % (365 * 24 * 60 * 60)) / (30 * 24 * 60 * 60)); // Approximation
        const days = Math.floor((this.seconds % (30 * 24 * 60 * 60)) / (24 * 60 * 60));
        const hours = Math.floor((this.seconds % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((this.seconds % (60 * 60)) / 60);
        const seconds = this.seconds % 60;

        if (years > 0) result += `${years}Y`;
        if (months > 0) result += `${months}M`;
        if (days > 0) result += `${days}D`;

        if (hours > 0 || minutes > 0 || seconds > 0 || this.nanos > 0) {
            result += 'T';
            if (hours > 0) result += `${hours}H`;
            if (minutes > 0) result += `${minutes}M`;
            if (seconds > 0 || this.nanos > 0) {
                result += `${seconds}`;
                if (this.nanos > 0) {
                    result += `.${this.nanos.toString().padStart(9, '0').replace(/0+$/, '')}`;
                }
                result += 'S';
            }
        }

        return result === 'P' ? 'PT0S' : result;
    }
}

export type DurationInput = Duration | number | string;

export function toDuration(input: DurationInput, isMinutes: boolean = false): Duration {
    if (input instanceof Duration) {
        return input;
    } else if (typeof input === 'number') {
        if (isMinutes) {
            return Duration.ofMinutes(input);
        }
        return Duration.ofSeconds(input);
    } else if (typeof input === 'string') {
        return Duration.parse(input);
    }
    throw new Error('Invalid duration input');
}
